import InfoIcon from '@mui/icons-material/Info';
import { Alert, Chip, Divider, Typography, Box, Paper } from '@mui/material';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { useNoteSectionSupervisor } from './useNoteSectionSupervisor';
import { useAppSelector } from '../../../../../lib/store';
import { selectScrollToHighlightTrigger } from '../../../../annotation/state/selector';
import { Annotation, Concept, Note } from '../../../../workspace-common/types/responseTypes';
import { RenderHighlightedText } from '../../../../workspace-common/utils/RenderHighlightedText';

const NoNoteSelected = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  return (
    <Paper
      sx={{
        height: '348px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(2),
      }}
    >
      <Typography variant="h5">
        {formatMessage({ id: 'workspaceCommon.noNoteSelected.title' })}
      </Typography>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
        {formatMessage({ id: 'workspaceCommon.noNoteSelected.description' })}
      </Typography>
    </Paper>
  );
};

interface NoteSectionSupervisorProps {
  selectedNote: Note | null;
  concepts: Concept[] | null;
  annotations: Annotation[] | null;
}

export const NoteSectionSupervisor = ({
  selectedNote,
  concepts,
  annotations,
}: NoteSectionSupervisorProps) => {
  const {
    annotationNumber,
    concept,
    isInAddEvidenceMode,
    stopEvidenceMode,
    handleMouseUp,
    handleAttach,
    pendingSpans,
    currentSpan,
    setCurrentSpan,
    highlightedTextSpans,
    textRef,
    selectedNoteId,
  } = useNoteSectionSupervisor({ concepts, annotations, selectedNote });

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const scrollToHighlightTrigger = useAppSelector(selectScrollToHighlightTrigger);

  useEffect(() => {
    if (!textRef.current) return;
    const firstHighlight = (textRef.current as HTMLElement).querySelector(
      '[data-highlight-start="true"]'
    );
    firstHighlight?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [scrollToHighlightTrigger]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'hidden' }}
    >
      {selectedNote ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box>
            <Typography variant="h5">
              {formatMessage({ id: 'workspaceCommon.noteSection.title' })}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                px: theme.spacing(3),
                pb: theme.spacing(3),
                pt: 0,
                m: theme.spacing(1),
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(4),
                overflowY: 'scroll',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(2),
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  pt: theme.spacing(3),
                }}
              >
                <Chip variant="outlined" label={selectedNote.noteType} />

                <Typography variant="body2">
                  {dayjs(selectedNote.noteDatetime).format('MM/DD/YYYY | HH:mm')}
                </Typography>

                <Typography variant="body2"> • </Typography>

                <Typography variant="body2">{selectedNote.noteClass}</Typography>

                <Typography variant="body2"> • </Typography>

                <Typography variant="body2">
                  {selectedNote.noteText.length.toLocaleString()}{' '}
                  {formatMessage({ id: 'workspaceCommon.noteSection.characters' })}
                </Typography>
              </Box>
              <Divider />
              {isInAddEvidenceMode && (
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Alert
                    severity="info"
                    iconMapping={{
                      info: <InfoIcon sx={{ width: 22, height: 22 }} />,
                    }}
                    sx={{
                      border: '1px solid',
                      borderColor: 'info.main',
                      alignItems: 'center',
                      maxHeight: '140px',
                      overflowY: 'scroll',
                      mt: theme.spacing(2),
                      mb: theme.spacing(2),
                      backgroundColor: theme.palette.blue[50],
                      width: '100%',
                      '& .MuiAlert-message': {
                        width: '100%',
                        flex: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: theme.spacing(2),
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-line', fontWeight: 500 }}
                        >
                          {formatMessage({ id: 'workspaceCommon.noteSection.addEvidenceMode' })}
                        </Typography>

                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {formatMessage(
                            { id: 'workspaceCommon.noteSection.addEvidenceMode.description' },
                            { conceptName: concept?.conceptName ?? '', annotationNumber }
                          )}
                        </Typography>
                      </Box>

                      <Box
                        id="selected-snippet-info"
                        sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {pendingSpans.length == 1
                            ? formatMessage(
                                { id: 'workspaceCommon.noteSection.selectedSnippetCount.singular' },
                                { snippetCount: pendingSpans.length }
                              )
                            : formatMessage(
                                { id: 'workspaceCommon.noteSection.selectedSnippetCount.plural' },
                                { snippetCount: pendingSpans.length }
                              )}
                        </Typography>
                        <Button
                          id="add-evidence-done-button"
                          displayLabel={formatMessage({
                            id: 'workspaceCommon.noteSection.addEvidenceDoneButton',
                          })}
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            bgcolor: theme.palette.primary.dark,
                          }}
                          onClickHandler={stopEvidenceMode}
                        />
                      </Box>
                    </Box>
                  </Alert>
                </Box>
              )}
              <Box>
                {currentSpan && (
                  <Paper
                    sx={{
                      position: 'fixed',
                      top: currentSpan.y,
                      left: currentSpan.x,
                      transform: 'translate(-50%, -100%)',
                      zIndex: 2000,
                      p: theme.spacing(2),
                      display: 'flex',
                      gap: theme.spacing(2),
                    }}
                  >
                    <Button
                      id="attach-button"
                      displayLabel={formatMessage({
                        id: 'workspaceCommon.noteSection.attachButton',
                      })}
                      variant="contained"
                      onClickHandler={handleAttach}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                      }}
                    />
                    <Button
                      id="cancel-button"
                      displayLabel={formatMessage({
                        id: 'workspaceCommon.noteSection.cancelAttachButton',
                      })}
                      variant="outlined"
                      onClickHandler={() => setCurrentSpan(null)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                      }}
                    />
                  </Paper>
                )}
                <Typography
                  ref={textRef as React.RefObject<HTMLDivElement>}
                  variant="body1"
                  component="div"
                  onMouseUp={handleMouseUp}
                >
                  <RenderHighlightedText
                    text={selectedNote.noteText}
                    highlightedTextSpans={highlightedTextSpans}
                    selectedNoteId={selectedNoteId ?? ''}
                  />
                </Typography>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: theme.spacing(1),
                  left: theme.spacing(1),
                  right: theme.spacing(1),
                  height: 40,
                  background: 'linear-gradient(to bottom, transparent, white)',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              />
            </Paper>
          </Box>
        </Box>
      ) : (
        <NoNoteSelected />
      )}
    </Box>
  );
};
